// The country occasions catalog: what a definition looks like, and the rules
// that turn one into concrete dates for a year.
//
// Pure — no storage, no baseline and no Node — so the build-time generator in
// scripts/generate-occasions.ts and the tests in test/ both read it directly.
//
// Every rule shape below is required by an official source. The research is in
// docs/country-occasions-phase-0.md. Nothing here was added on speculation:
// extend the union when a country genuinely needs a shape it does not have.

import { chineseLunarUtc, qingmingUtc } from './chinese-lunar'

export type Weekday =
    'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'

const WEEKDAYS: Weekday[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
]

// Which years a rule fires in. Absent means every year. Inauguration Day is
// every fourth year after 1965; China's extra Spring Festival eve and second
// Labour Day only exist from 2025.
export interface YearFilter {
    everyNthYear: number
    anchorYear: number
    // Inclusive. Labour Day's second day in China only exists from 2025.
    fromYear?: number
    untilYear?: number
}

export type DateRule
    = { kind: 'fixed', month: number, day: number, years?: YearFilter }
    // `offsetDays` shifts off the weekday it lands on, which is the only way to
    // say New York's election day: the Tuesday next after the first Monday in
    // November, which is not the same as the first Tuesday.
    | { kind: 'nth_weekday', month: number, weekday: Weekday, nth: number, offsetDays?: number, years?: YearFilter }
    | { kind: 'last_weekday', month: number, weekday: Weekday, years?: YearFilter }
    // The last <weekday> strictly before a fixed date. Saxony's Buß- und Bettag
    // is the Wednesday before 23 November, and when the 23rd is itself a
    // Wednesday the holiday is the week before it.
    | { kind: 'weekday_before', month: number, day: number, weekday: Weekday, years?: YearFilter }
    // Days away from another date. `base` is 'easter' or another occasion's id
    // in the same country. One shape covers both the German Easter cycle and
    // California's day after Thanksgiving, so there is no separate Easter rule.
    | { kind: 'offset', base: string, offsetDays: number, years?: YearFilter }
    // PRC Spring Festival, Dragon Boat, Mid-Autumn. `month`/`day` are lunar.
    | { kind: 'chinese_lunar', month: number, day: number, years?: YearFilter }
    // PRC Qingming: the solar term, 4 or 5 April, not a lunar month-day.
    | { kind: 'qingming', years?: YearFilter }

export type Shift =
    'previous_friday' | 'previous_saturday' | 'next_monday' | 'next_tuesday' | 'none'

// The day offices actually close when the legal date lands on a weekend. This
// belongs to the occasion and never to the country: California moves seven
// named dates off Sunday and only Veterans Day off Saturday, while Germany
// moves nothing at all.
export interface ObservedRule {
    kind: 'weekend_shift'
    saturday?: Shift
    sunday?: Shift
}

// 'Holiday' does not mean 'day off'. California has named Juneteenth in
// Government Code 6700 since 2023 and still opens its offices, so a single
// isHoliday boolean cannot carry this.
export type OccasionType
    = 'public_holiday' // Statutory. Offices close.
    | 'named_in_law'   // Named in the holiday statute. Offices stay open.
    | 'commemorative'  // Officially recognised, ceremonial only.

// A region is anything smaller than a country that can hold a holiday of its
// own. The researched cases need three levels and stop there: a German state,
// a single city, and one named area that spans a federal district and parts of
// two states. `parent` is another region's code, absent at the top level.
// A note carries both languages for the same reason `names` does: the English
// side is the maintainer's record of what a source said, and only the Persian
// side is ever shown, because the app is Persian.
export interface CatalogNote {
    en?: string
    fa?: string
}

export interface CatalogRegion {
    code: string
    level: 'state' | 'city' | 'area'
    names: { en: string, fa?: string, local?: string }
    parent?: string
    note?: CatalogNote
    sourceUrl?: string
}

export interface CatalogCountry {
    code: string
    // Holiday-file version for this country. The index version can move when
    // only the directory changes; this one moves when the country's own file
    // does, so an app that already holds NL does not re-download it because US
    // gained a state. Absent means the index version, which is what older
    // feeds shipped.
    version?: number
    // The country's name in a URL: /occasions/country/germany. Kept apart from
    // `code` because a route is read by people and by search engines, and DE is
    // neither.
    slug: string
    names: { en: string, fa?: string, local?: string }
    regions: CatalogRegion[]
    // Which levels of government were actually read. An occasion is only ever
    // claimed for a region on this list.
    researched: string[]
    note?: CatalogNote
    sourceUrl?: string
    verifiedAt?: string
}

export interface CatalogOccasion {
    id: string
    slug: string
    country: string
    names: { en: string, fa?: string, local?: string }
    type: OccasionType
    // Region codes from countries.json. Absent means the whole country.
    regions?: string[]
    // Absent means the occasion has no rule, so it needs an explicit override
    // for every year it happens — a one-off, or a date only an authority sets.
    dateRule?: DateRule
    observedRule?: ObservedRule
    // Another occasion in the same country this one stands in for, in the
    // regions it applies to. South Dakota's Native Americans' Day occupies the
    // same date as federal Columbus Day; showing both would be the same event
    // twice. The calendar keeps this one and drops the replaced id.
    replaces?: string
    // For the case where the date is certain but the map is not. Bavaria's
    // Mariä Himmelfahrt is always 15 August, and applies only in the
    // municipalities a statistical office picks from the latest census.
    applicability?: { note: CatalogNote, sourceUrl: string }
    description?: CatalogNote
    sourceUrl?: string
    verifiedAt?: string
}

export interface CatalogOverride {
    occasionId: string
    year: number
    date?: string
    observedDate?: string
    reason?: string
    sourceUrl: string
    verifiedAt: string
}

export interface CatalogOccurrence {
    occasionId: string
    date: string
    observedDate?: string
    status: 'official' | 'tentative' | 'estimated'
    source: 'rule' | 'authority' | 'override'
    verifiedAt?: string
    regions?: string[]
}

const DAY = 86400000
const utc = (y: number, m: number, d: number) => Date.UTC(y, m - 1, d)
const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10)
const parseIso = (date: string) => Date.parse(`${date}T00:00:00Z`)
const weekdayOf = (ms: number) => new Date(ms).getUTCDay()

// Western Easter, by the anonymous Gregorian computus. Germany is the only
// researched user of it and follows the Western date.
export function easterSunday(year: number): number {
    const a = year % 19
    const b = Math.floor(year / 100)
    const c = year % 100
    const d = Math.floor(b / 4)
    const e = b % 4
    const f = Math.floor((b + 8) / 25)
    const g = Math.floor((b - f + 1) / 3)
    const h = (19 * a + b - d - g + 15) % 30
    const i = Math.floor(c / 4)
    const k = c % 4
    const l = (32 + 2 * e + 2 * i - h - k) % 7
    const m = Math.floor((a + 11 * h + 22 * l) / 451)
    const n = h + l - 7 * m + 114
    return utc(year, Math.floor(n / 31), (n % 31) + 1)
}

const nthWeekday = (year: number, month: number, weekday: Weekday, nth: number) => {
    const first = utc(year, month, 1)
    const delta = (WEEKDAYS.indexOf(weekday) - weekdayOf(first) + 7) % 7
    return first + (delta + (nth - 1) * 7) * DAY
}

// Day 0 of the next month is the last day of this one, whatever its length.
const lastWeekday = (year: number, month: number, weekday: Weekday) => {
    const last = utc(year, month + 1, 0)
    return last - ((weekdayOf(last) - WEEKDAYS.indexOf(weekday) + 7) % 7) * DAY
}

// Strictly before, so a date that already falls on the weekday sends the answer
// back a full week — which is what Buß- und Bettag does.
const weekdayBefore = (year: number, month: number, day: number, weekday: Weekday) => {
    const from = utc(year, month, day)
    const delta = ((weekdayOf(from) - WEEKDAYS.indexOf(weekday) + 7) % 7) || 7
    return from - delta * DAY
}

// The legal date, following `offset` chains through the country's other
// occasions. Returns undefined when the rule does not fire that year, or when
// the chain is broken or circular — validate() reports those properly.
function ruledDate(
    occasion: CatalogOccasion,
    year: number,
    byId: Map<string, CatalogOccasion>,
    seen: Set<string> = new Set(),
): number | undefined {
    const rule = occasion.dateRule
    if (!rule || seen.has(occasion.id)) return undefined
    seen.add(occasion.id)

    const years = 'years' in rule ? rule.years : undefined
    if (years) {
        if (years.fromYear !== undefined && year < years.fromYear) return undefined
        if (years.untilYear !== undefined && year > years.untilYear) return undefined
        if ((year - years.anchorYear) % years.everyNthYear !== 0) return undefined
    }

    if (rule.kind === 'fixed') {
        return utc(year, rule.month, rule.day)
    }
    if (rule.kind === 'nth_weekday') {
        return nthWeekday(year, rule.month, rule.weekday, rule.nth)
            + (rule.offsetDays ?? 0) * DAY
    }
    if (rule.kind === 'last_weekday') return lastWeekday(year, rule.month, rule.weekday)
    if (rule.kind === 'weekday_before') {
        return weekdayBefore(year, rule.month, rule.day, rule.weekday)
    }

    if (rule.kind === 'offset') {
        if (rule.base === 'easter') return easterSunday(year) + rule.offsetDays * DAY
        const base = byId.get(rule.base)
        const from = base && ruledDate(base, year, byId, seen)
        return from === undefined ? undefined : from + rule.offsetDays * DAY
    }
    if (rule.kind === 'chinese_lunar') return chineseLunarUtc(year, rule.month, rule.day)
    if (rule.kind === 'qingming') return qingmingUtc(year)

    // A shape this build does not know, from a catalog newer than the app. The
    // occasion is skipped rather than given an invented date, and the reason is
    // said once per shape so a console shows it without filling up.
    warnUnknown((rule as { kind: string }).kind, occasion.id)
    return undefined
}

const warned = new Set<string>()
function warnUnknown(kind: string, id: string) {
    if (warned.has(kind)) return
    warned.add(kind)
    console.warn(`occasion-catalog: unknown date rule '${kind}' (first seen on ${id}). `
        + 'Those occasions are skipped. Update the app to see them.')
}

// The day off, when the legal date lands on a weekend the rule cares about.
// Returns undefined when nothing moves.
function shiftedDate(ms: number, rule: ObservedRule): number | undefined {
    const day = weekdayOf(ms)
    const shift = day === 6 ? rule.saturday : day === 0 ? rule.sunday : undefined
    if (!shift || shift === 'none') return undefined
    if (shift === 'previous_friday') return ms - (day === 6 ? 1 : 2) * DAY
    // Only ever set on `sunday` — the Netherlands moves King's Day back a day
    // rather than forward, so it never lands on a Sunday.
    if (shift === 'previous_saturday') return ms - DAY
    // For a holiday whose substitute Monday is already taken by the holiday
    // before it. Britain gives Boxing Day the Tuesday when Christmas Day has
    // the Monday, and the same for Scotland's 2nd January after New Year's Day.
    if (shift === 'next_tuesday') return ms + (day === 0 ? 2 : 3) * DAY
    return ms + (day === 0 ? 1 : 2) * DAY
}

// One year of concrete dates for one country, sorted by date.
//
// The resolution order is the plan's. The rule gives the legal date, the
// observed rule gives the day off, and an official override replaces either of
// them field by field — so an override that only corrects the observed day
// leaves the legal date alone, and an override that moves the legal date still
// gets shifted off a weekend.
export function occurrencesFor(
    definitions: CatalogOccasion[],
    overrides: CatalogOverride[],
    year: number,
): CatalogOccurrence[] {
    const byId = new Map(definitions.map(o => [o.id, o]))
    const out: CatalogOccurrence[] = []

    for (const occasion of definitions) {
        const override = overrides.find(o => o.occasionId === occasion.id && o.year === year)
        const ruled = ruledDate(occasion, year, byId)
        const date = override?.date ?? (ruled === undefined ? undefined : iso(ruled))
        if (!date) continue

        const shifted = occasion.observedRule
            ? shiftedDate(parseIso(date), occasion.observedRule)
            : undefined
        const observedDate = override?.observedDate
            ?? (shifted === undefined ? undefined : iso(shifted))

        out.push({
            occasionId: occasion.id,
            date,
            ...(observedDate ? { observedDate } : {}),
            status: 'official',
            source: override?.date ? 'override' : 'rule',
            ...(occasion.verifiedAt ? { verifiedAt: occasion.verifiedAt } : {}),
            ...(occasion.regions ? { regions: occasion.regions } : {}),
        })
    }
    return out.sort((a, b) => a.date.localeCompare(b.date))
}

const MONTH_LENGTHS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

// Every shape this build can resolve. Checked by name rather than by type, so
// validate() catches a rule from a newer catalog that the compiler never saw.
const KNOWN_RULES = ['fixed', 'nth_weekday', 'last_weekday', 'weekday_before', 'offset', 'chinese_lunar', 'qingming']

// Everything that would make a generated year wrong or silently empty. The
// generator refuses to write a country while this returns anything.
export function validate(
    definitions: CatalogOccasion[],
    overrides: CatalogOverride[],
): string[] {
    const problems: string[] = []
    const byId = new Map<string, CatalogOccasion>()

    for (const o of definitions) {
        if (byId.has(o.id)) problems.push(`duplicate occasion id: ${o.id}`)
        byId.set(o.id, o)
    }
    for (const o of overrides) {
        if (!byId.has(o.occasionId)) problems.push(`override for unknown occasion: ${o.occasionId}`)
    }
    for (const o of definitions) {
        if (o.replaces && !byId.has(o.replaces)) {
            problems.push(`${o.id} replaces unknown occasion: ${o.replaces}`)
        }
    }

    for (const o of definitions) {
        const rule = o.dateRule
        if (!rule) {
            // A rule-less occasion only ever happens on the years its overrides
            // name. With none, it is dead data.
            if (!overrides.some(v => v.occasionId === o.id)) {
                problems.push(`${o.id} has no date rule and no override, so it never happens`)
            }
            continue
        }
        if (!KNOWN_RULES.includes(rule.kind)) {
            problems.push(`${o.id} uses a date rule this build cannot resolve: ${rule.kind}`)
            continue
        }
        if (rule.kind !== 'offset' && rule.kind !== 'qingming' && (rule.month < 1 || rule.month > 12)) {
            problems.push(`${o.id} has month ${rule.month}`)
        }
        if ((rule.kind === 'fixed' || rule.kind === 'weekday_before')
            && (rule.day < 1 || rule.day > MONTH_LENGTHS[rule.month - 1])) {
            problems.push(`${o.id} has an impossible date: ${rule.month}/${rule.day}`)
        }
        if (rule.kind === 'chinese_lunar' && (rule.day < 1 || rule.day > 30)) {
            problems.push(`${o.id} has an impossible lunar date: ${rule.month}/${rule.day}`)
        }
        if (rule.kind === 'nth_weekday' && (rule.nth < 1 || rule.nth > 5)) {
            problems.push(`${o.id} asks for weekday number ${rule.nth}`)
        }
        if ('years' in rule && rule.years && rule.years.everyNthYear < 1) {
            problems.push(`${o.id} repeats every ${rule.years.everyNthYear} years`)
        }
        if (rule.kind === 'offset') problems.push(...offsetProblems(o, rule.base, byId))
    }
    return problems
}

// Walks an offset chain to its root, reporting a missing base or a circle.
function offsetProblems(
    occasion: CatalogOccasion,
    base: string,
    byId: Map<string, CatalogOccasion>,
): string[] {
    const seen = new Set([occasion.id])
    let at = base
    while (at !== 'easter') {
        if (seen.has(at)) return [`${occasion.id} offsets in a circle through ${at}`]
        const next = byId.get(at)
        if (!next) return [`${occasion.id} offsets from unknown base: ${at}`]
        seen.add(at)
        if (next.dateRule?.kind !== 'offset') return []
        at = next.dateRule.base
    }
    return []
}
